import React, { useCallback, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { KeyboardAwareFlatList, } from 'utils/packages/rnKeyboardAwareScrollview';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { AppView, LineSeparator } from 'design-system';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useKeyboardAdjustPan } from 'hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import Text from 'design-system/Text';
import { CustomSplitType } from 'utils/types/navigation';
import FlatListItemCard from 'design-system/FlatListItemCard';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import Amount from 'design-system/Amount';
import { useKeyboard } from 'hooks/useKeyboard';
import { CustomSplitRow } from './components/CustomSplitRow';
import { CustomSplitHeader } from './components/CustomSplitHeader';
import { AmountSplit } from './utils/amountSplit';
import { isAndroid } from '../../constants';
const AnimatedKeyboardAwareFlatList = Animated.createAnimatedComponent(KeyboardAwareFlatList);
const extraScrollHeight = rem(24);
const GroupCustomSplitScreen = withAnimatedNavigationHeader(({ route, navigation, onScroll }) => {
    useKeyboardAdjustPan();
    const { users, split, splitType, total, currency, paidBy, onSave, isDefaultSplit, } = route.params;
    const absTotal = Math.abs(total);
    const styles = useStyles(styleSet);
    const [selectedType, setSelectedType] = useState(splitType);
    const [percentageSplit, setPercentageSplit] = useState(() => AmountSplit.toPercentageSplit(total, split));
    const [shareSplit, setShareSplit] = useState(() => AmountSplit.toShareSplit(total, split));
    const [amountSplit, setAmountSplit] = useState(split);
    const currentSplit = useMemo(() => {
        switch (selectedType) {
            case CustomSplitType.SHARE:
                return AmountSplit.fromShareSplit(total, shareSplit);
            case CustomSplitType.PERCENTAGE:
                return AmountSplit.fromPercentageSplit(total, percentageSplit);
            default:
                return Object.fromEntries(Object.entries(amountSplit).map(([userId, amount]) => [
                    userId,
                    Number(amount).toFixed(2),
                ]));
        }
    }, [amountSplit, percentageSplit, selectedType, shareSplit, total]);
    const selectedUsers = useMemo(() => Object.entries(currentSplit)
        .filter(([, share]) => Number(share) > 0)
        .map(([userId]) => Number(userId)), [currentSplit]);
    const userIds = useMemo(() => users.map((u) => u.id), [users]);
    const editQueue = useRef([]);
    const unEditedUsers = useRef([]);
    const addToQueue = useCallback((userId) => {
        if (!editQueue.current.length) {
            const currentEditIndex = userIds.indexOf(userId);
            editQueue.current = [
                ...userIds.slice(currentEditIndex + 1),
                ...userIds.slice(0, currentEditIndex + 1),
            ];
            if (isDefaultSplit) {
                unEditedUsers.current = selectedUsers;
            }
        }
        else {
            editQueue.current = [
                ...editQueue.current.filter((id) => id !== userId),
                userId,
            ];
        }
        if (isDefaultSplit) {
            unEditedUsers.current = unEditedUsers.current.filter((id) => userId !== id);
        }
    }, [isDefaultSplit, selectedUsers, userIds]);
    const onChangeType = useCallback((type) => {
        if (type === selectedType)
            return;
        switch (type) {
            case CustomSplitType.SHARE:
                setShareSplit(AmountSplit.toShareSplit(total, currentSplit));
                break;
            case CustomSplitType.PERCENTAGE:
                setPercentageSplit(AmountSplit.toPercentageSplit(total, currentSplit));
                break;
            default:
                setAmountSplit(currentSplit);
                break;
        }
        setSelectedType(type);
    }, [currentSplit, selectedType, total]);
    const onChangeSplit = useCallback((userId, value) => {
        if (selectedType === CustomSplitType.SHARE) {
            const numericValue = value.replace(/^0+(?!\.)/, '') || '0';
            if (Number.isNaN(Number(numericValue)))
                return;
            addToQueue(userId);
            setShareSplit((prev) => ({
                ...prev,
                [userId]: numericValue,
            }));
            return;
        }
        addToQueue(userId);
        const unEdited = unEditedUsers.current;
        if (selectedType === CustomSplitType.AMOUNT) {
            setAmountSplit((prev) => AmountSplit.autoCalculateSplit({
                editQueue,
                unEditedUsers: unEdited,
                max: absTotal,
                split: { ...prev, [userId]: value },
                splitType: CustomSplitType.AMOUNT,
            }));
        }
        else if (selectedType === CustomSplitType.PERCENTAGE) {
            setPercentageSplit((prev) => AmountSplit.autoCalculateSplit({
                editQueue,
                unEditedUsers: unEdited,
                max: 100,
                split: { ...prev, [userId]: value },
                splitType: CustomSplitType.PERCENTAGE,
            }));
        }
    }, [absTotal, addToQueue, selectedType]);
    const ListHeaderComponent = useMemo(() => (<CustomSplitHeader split={AmountSplit.toPercentageSplit(total, currentSplit)} selectedType={selectedType} setSelectedType={onChangeType} users={users}/>), [currentSplit, onChangeType, selectedType, total, users]);
    const ListFooterComponent = useMemo(() => {
        if (selectedType !== CustomSplitType.AMOUNT)
            return null;
        return (<FlatListItemCard index={1} itemsLength={2} style={styles.card}>
          <LineSeparator noMargin/>
          <View style={styles.totalSpend}>
            <Text Text-16>Total amount</Text>
            <Amount Numbers-16 value={{
                amount: Math.abs(absTotal),
                currency,
            }}/>
          </View>
        </FlatListItemCard>);
    }, [currency, selectedType, styles.card, styles.totalSpend, absTotal]);
    const renderItem = useCallback(({ item, index }) => {
        const itemsLength = users.length +
            (selectedUsers.length ? 1 : 0) +
            (selectedType === CustomSplitType.AMOUNT ? 1 : 0);
        return (<CustomSplitRow index={index + (selectedUsers.length ? 1 : 0)} itemsLength={itemsLength} item={item} percentageSplit={percentageSplit} shareSplit={shareSplit} amountSplit={amountSplit} onChangeSplit={onChangeSplit} selectedType={selectedType} currency={currency} maxAmount={absTotal}/>);
    }, [
        absTotal,
        selectedType,
        selectedUsers,
        users.length,
        percentageSplit,
        shareSplit,
        amountSplit,
        onChangeSplit,
        currency,
    ]);
    const { keyboardHeight } = useKeyboard();
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING + (isAndroid ? keyboardHeight : 0), paddingHorizontalStyle);
    const extraData = useMemo(() => [
        percentageSplit,
        amountSplit,
        shareSplit,
        selectedType,
        selectedUsers.length,
        users.length,
    ], [
        amountSplit,
        percentageSplit,
        selectedType,
        selectedUsers.length,
        shareSplit,
        users.length,
    ]);
    const onSavePress = useCallback(() => {
        onSave(currentSplit, selectedType);
        navigation.goBack();
    }, [currentSplit, onSave, selectedType]);
    return (<AppView withFrame={false}>
        <AnimatedKeyboardAwareFlatList extraData={extraData} data={users} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} onScroll={onScroll} keyboardShouldPersistTaps="handled" renderItem={renderItem} extraScrollHeight={extraScrollHeight} contentContainerStyle={contentContainerStyle} ListHeaderComponent={ListHeaderComponent} ListFooterComponent={ListFooterComponent}/>
        <Button brand floating disabled={!selectedUsers.length ||
            (selectedUsers.length === 1 && selectedUsers[0] === paidBy.id)} title="Save" onPress={onSavePress}/>
      </AppView>);
}, ({ colors }) => ({
    title: 'Custom split',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    flex: {
        flex: 1,
    },
    totalSpend: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        margin: rem(16),
    },
    row: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: rem(8),
    },
    card: {
        backgroundColor: colors.cards.onDark,
    },
}));
export default GroupCustomSplitScreen;
